import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, MapPin, Loader2, Mail, Lock, ArrowRight, ChevronDown, Star, Shield, Play, Phone, TrendingUp, Award, BarChart3, Eye, Globe, DollarSign, CheckCircle2, ArrowUpRight, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── DATA ─── */
const FAQ_DATA = [
  { q: "Is it really free?", a: "Your score, top 3 issues, and competitor comparison are completely free. The full fix plan with all 8 issues, copy-paste templates, and 90-day roadmap unlocks for $27 — a one-time fee, no subscription." },
  { q: "How long does it take?", a: "About 30 seconds to generate your report. Most business owners spend 5-10 minutes reading through it. The insights stay with you forever." },
  { q: "Do I need to be tech-savvy?", a: "Not at all. The report is written for business owners, not SEO experts. Every issue comes with a plain-English explanation and a step-by-step fix." },
  { q: "What if my score is low?", a: "That's actually good news — it means there's significant room to improve, and the fixes are usually straightforward. Our clients with the lowest starting scores often see the fastest results." },
  { q: "What happens after I see my score?", a: "Nothing unless you want it to. We'll email you a copy of your free report. If you want help fixing the issues, we offer done-for-you monthly plans starting at $97/month." },
];

/* ─── LIVE COUNTER ─── */
function useLiveCounter(start = 4000) {
  const [count, setCount] = useState(start);
  useEffect(() => {
    const t0 = Date.now();
    const interval = setInterval(() => {
      const elapsed = (Date.now() - t0) / 1000;
      setCount(start + Math.floor(elapsed / 47));
    }, 1000);
    return () => clearInterval(interval);
  }, [start]);
  return count.toLocaleString();
}

/* ─── AUTOCOMPLETE CACHE ─── */
const autocompleteCache = {};

/* ─── ANIMATED COUNTER ─── */
function useCountUp(target, duration = 2000, trigger = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let start = 0;
    const t0 = performance.now();
    const step = (now) => {
      const progress = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, trigger]);
  return val;
}

/* ─── INTERSECTION OBSERVER HOOK ─── */
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ─── HERO FORM ─── */
function HeroForm({ variant = "hero" }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const debounceRef = useRef(null);
  const [businessQuery, setBusinessQuery] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill email from auth context
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user]);

  const handleInputChange = useCallback((value) => {
    setBusinessQuery(value);
    setSelectedBusiness(null);
    clearTimeout(debounceRef.current);
    if (value.length < 2) { setPredictions([]); setSearching(false); return; }
    const cacheKey = value.toLowerCase().trim();
    if (autocompleteCache[cacheKey]) { setPredictions(autocompleteCache[cacheKey]); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/autocomplete`, { input: value });
        const results = res.data?.predictions || [];
        autocompleteCache[cacheKey] = results;
        setPredictions(results);
      } catch { setPredictions([]); }
      setSearching(false);
    }, 200);
  }, []);

  const selectBusiness = async (pred) => {
    setPredictions([]);
    setBusinessQuery(pred.main_text || pred.description);
    try {
      const res = await axios.post(`${API}/place-details`, { place_id: pred.place_id });
      const biz = res.data;
      setSelectedBusiness({
        name: biz.name,
        address: biz.address,
        place_id: pred.place_id,
        types: biz.types,
        category: biz.category,
        city: biz.city || "",
      });
    } catch { toast.error("Could not load business details."); }
  };

  const handleSubmit = async () => {
    if (!selectedBusiness) { toast.error("Please search and select your business."); return; }
    if (!email.trim() || !email.includes("@")) { toast.error("Please enter a valid email address."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/capture`, {
        email: email.trim().toLowerCase(),
        business_name: selectedBusiness.name,
        city: selectedBusiness.city || "",
        place_id: selectedBusiness.place_id,
        phone_number: phoneNumber.trim(),
        source: "landing_hero",
      });
    } catch { /* best-effort */ }
    sessionStorage.setItem("landing_prefill", JSON.stringify({
      business: selectedBusiness,
      email: email.trim().toLowerCase(),
      phoneNumber: phoneNumber.trim(),
      city: selectedBusiness.city || "",
    }));
    sessionStorage.setItem("audit_lead", JSON.stringify({
      name: "",
      email: email.trim().toLowerCase(),
      phone: phoneNumber.trim(),
      phone_number: phoneNumber.trim(),
      city: selectedBusiness.city || "",
    }));
    navigate("/audit");
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const isDark = variant === "hero" || variant === "final";

  return (
    <div className="w-full max-w-md" data-testid={`${variant}-audit-form`}>
      {/* Business Name */}
      <div className="relative mb-3">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
        {searching && <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6366f1] animate-spin z-10" />}
        <input
          type="text"
          placeholder="Your business name..."
          value={businessQuery}
          onChange={e => handleInputChange(e.target.value)}
          className="w-full h-13 pl-11 pr-10 rounded-xl text-base bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#6366f1]/40 focus:border-[#6366f1]"
          style={{ fontSize: 16, height: 52 }}
          data-testid={`${variant}-business-input`}
        />
        {/* Dropdown */}
        {(predictions.length > 0 || (searching && businessQuery.length >= 2)) && (
          <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
            {searching && predictions.length === 0 && (
              <div className="p-3 text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Searching...</div>
            )}
            {predictions.map((pred, i) => (
              <button key={i} onClick={() => selectBusiness(pred)}
                className="w-full flex items-start gap-3 p-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0"
                data-testid={`${variant}-prediction-${i}`}>
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 truncate">{pred.main_text || pred.description}</p>
                  <p className="text-xs text-slate-400 truncate">{pred.secondary_text || ""}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Selected confirmation */}
      {selectedBusiness && (
        <div className="mb-3 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
          <p className="text-sm font-medium text-emerald-400">{selectedBusiness.name}</p>
          <p className="text-xs text-slate-400">{selectedBusiness.address}</p>
        </div>
      )}

      {/* Email */}
      <div className="relative mb-2">
        <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
        <input
          type="email"
          placeholder="Your email address..."
          value={email}
          onChange={e => setEmail(e.target.value)}
          readOnly={!!user?.email}
          className={`w-full h-13 pl-11 pr-4 rounded-xl text-base border text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#6366f1]/40 focus:border-[#6366f1] ${user?.email ? "bg-slate-50 border-slate-200 cursor-default" : "bg-white border-slate-200"}`}
          style={{ fontSize: 16, height: 52 }}
          data-testid={`${variant}-email-input`}
        />
        {user?.email && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-emerald-500 font-medium">Logged in</span>}
      </div>

      {/* Phone (optional) */}
      <div className="relative mb-3">
        <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
        <input
          type="tel"
          placeholder="Phone number (optional)"
          value={phoneNumber}
          onChange={e => setPhoneNumber(e.target.value)}
          className="w-full h-13 pl-11 pr-4 rounded-xl text-base bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#6366f1]/40 focus:border-[#6366f1]"
          style={{ fontSize: 16, height: 48 }}
          data-testid={`${variant}-phone-input`}
        />
        <p className="text-xs text-slate-400 mt-1 pl-1">Get a free 5-minute consultation call with your results</p>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full h-13 rounded-xl font-bold text-white text-base flex items-center justify-center gap-2 transition-all duration-200 disabled:opacity-60"
        style={{ background: "#6366f1", height: 52 }}
        onMouseOver={e => { if (!submitting) e.currentTarget.style.background = "#4f46e5"; }}
        onMouseOut={e => { e.currentTarget.style.background = "#6366f1"; }}
        data-testid={`${variant}-submit-btn`}
      >
        {submitting ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing your business...</>
        ) : (
          <>Run My Free Audit <ArrowRight className="w-4 h-4" /></>
        )}
      </button>

      {/* Trust signals */}
      <div className="mt-4 space-y-1.5">
        <p className={`text-xs flex items-center gap-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          <Lock className="w-3 h-3" /> No credit card &middot; No spam
        </p>
        <p className={`text-xs flex items-center gap-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          <Star className="w-3 h-3" /> Used by 4,000+ local businesses
        </p>
        <p className={`text-xs ${isDark ? "text-slate-500" : "text-slate-400"}`}>
          Canada &amp; United States
        </p>
      </div>
    </div>
  );
}

/* ─── SECTION FADE-IN WRAPPER ─── */
function FadeIn({ children, className = "", delay = 0 }) {
  const [ref, inView] = useInView(0.15);
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(24px)",
        transition: `opacity 700ms ease-out ${delay}ms, transform 700ms ease-out ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}


/* ─── MAIN COMPONENT ─── */
export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const liveCount = useLiveCounter();
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  /* Counter animation for $8,400 section */
  const [costRef, costInView] = useInView(0.4);
  const costCount = useCountUp(8400, 2000, costInView);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.title = "RankifyLocal — See Why Competitors Outrank You";
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#060D12" }} data-testid="story-landing-page">

      {/* ═══ NAV ═══ */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? "rgba(6,13,18,0.95)" : "transparent",
          borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "1px solid transparent",
          backdropFilter: scrolled ? "blur(16px) saturate(180%)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(16px) saturate(180%)" : "none",
        }}
        data-testid="landing-nav"
      >
        <div className="max-w-6xl mx-auto px-5 flex justify-between items-center h-16">
          <Link to="/"><Logo size="default" theme="dark" /></Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Link
                to="/dashboard"
                className="text-sm font-medium text-slate-400 hover:text-white transition-colors"
                data-testid="nav-dashboard-link"
              >
                Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                className="text-sm font-medium text-slate-400 hover:text-white transition-colors"
                data-testid="nav-login-link"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* ═══ SECTION 1: HERO ═══ */}
      <section
        className="relative pt-24 pb-16 lg:pt-0 lg:pb-0 lg:min-h-screen lg:flex lg:items-center"
        style={{ overflow: "hidden" }}
        data-testid="hero-section"
      >
        {/* Ambient background glow */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[600px] rounded-full opacity-[0.07]" style={{ background: "radial-gradient(circle, #6366f1, transparent 70%)" }} />
        <div className="absolute top-0 left-0 right-0 bottom-0" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />

        <div className="relative z-10 max-w-6xl mx-auto px-5 w-full py-8 lg:py-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* Left column: copy */}
            <div className="lg:col-span-7">
              <p className="text-xs font-bold tracking-[0.25em] uppercase mb-5" style={{ color: "#3ECF8E" }} data-testid="hero-overline">
                Free Local SEO Audit
              </p>
              <h1
                className="font-black tracking-tight text-white leading-[1.05]"
                style={{ fontSize: "clamp(32px, 6vw, 60px)" }}
                data-testid="hero-headline"
              >
                Your Competitors Are<br className="hidden sm:inline" />
                {" "}Stealing Your Customers
              </h1>
              <p className="mt-5 text-lg sm:text-xl leading-relaxed" style={{ color: "#94A3B8" }}>
                See exactly where you rank, who's outperforming you, and what it's costing you — free, in 30 seconds.
              </p>

              {/* Arctic Air proof point — right under headline */}
              <div className="mt-6 flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: "rgba(62,207,142,0.08)", border: "1px solid rgba(62,207,142,0.15)" }} data-testid="hero-proof-point">
                <TrendingUp className="w-5 h-5 shrink-0" style={{ color: "#3ECF8E" }} />
                <p className="text-sm" style={{ color: "#94A3B8" }}>
                  <span className="font-bold text-white">Arctic Air HVAC</span> went from #8 to #1 in 67 days after their audit
                </p>
              </div>

              {/* Mobile: form right here for mobile-first */}
              <div className="lg:hidden mt-8">
                <div className="rounded-2xl p-5 sm:p-6" style={{ background: "rgba(27,58,75,0.35)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(12px)" }}>
                  <HeroForm variant="hero" />
                </div>
              </div>

              {/* Live counter — below form on mobile, below copy on desktop */}
              <p className="mt-6 text-xs" style={{ color: "#64748B" }} data-testid="hero-live-counter">
                Join <strong className="text-slate-300">{liveCount}+</strong> businesses who discovered their score
              </p>
            </div>

            {/* Right column: form (desktop only) */}
            <div className="hidden lg:block lg:col-span-5">
              <div className="relative">
                {/* Glow behind form */}
                <div className="absolute -inset-4 rounded-3xl opacity-20" style={{ background: "radial-gradient(circle, #6366f1, transparent 70%)", filter: "blur(40px)" }} />
                <div className="relative rounded-2xl p-7" style={{ background: "rgba(27,58,75,0.4)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(16px)" }}>
                  <HeroForm variant="hero" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* ═══ SECTION 2: THE PROBLEM ═══ */}
      <section className="relative" style={{ background: "#0A141A", padding: "80px 0" }} data-testid="pain-section">
        {/* Divider line */}
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(99,102,241,0.3), transparent)" }} />

        <div className="max-w-3xl mx-auto px-5">
          <FadeIn>
            <p className="text-xs font-bold tracking-[0.25em] uppercase mb-4" style={{ color: "#3ECF8E" }}>
              What's happening right now
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight mb-4">
              Someone just searched for<br className="hidden sm:inline" /> what you offer
            </h2>
            <p className="text-base sm:text-lg mb-10" style={{ color: "#94A3B8", lineHeight: 1.8 }}>
              They opened Google. Your competitors appeared at the top. You didn't appear at all. That customer called your competitor, booked with them, and never knew you existed.
            </p>
          </FadeIn>

          {/* Competitor leaderboard visual */}
          <FadeIn delay={150}>
            <div className="rounded-xl overflow-hidden" style={{ background: "rgba(27,58,75,0.25)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="pain-competitor-cards">
              {/* Header bar */}
              <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="w-2 h-2 rounded-full" style={{ background: "#3ECF8E" }} />
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "#64748B" }}>Google Maps Results — Your City</span>
              </div>

              <div className="p-3 sm:p-4 space-y-2">
                {[
                  { rank: 1, name: "Riverside Plumbing", rating: "4.8", reviews: "203", color: "#3ECF8E" },
                  { rank: 2, name: "City Pro Plumbers", rating: "4.6", reviews: "156", color: "#3ECF8E" },
                  { rank: 3, name: "QuickFix Plumbing", rating: "4.5", reviews: "89", color: "#3ECF8E" },
                ].map((c) => (
                  <div key={c.rank} className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg" style={{ background: "rgba(255,255,255,0.03)", borderLeft: `3px solid ${c.color}` }}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0" style={{ background: c.color }}>
                      {c.rank}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{c.name}</p>
                      <div className="flex items-center gap-1 mt-0.5">
                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                        <span className="text-xs" style={{ color: "#64748B" }}>{c.rating} &middot; {c.reviews} reviews</span>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold px-2 py-1 rounded-full shrink-0" style={{ background: "rgba(62,207,142,0.1)", color: "#3ECF8E" }}>
                      Visible
                    </span>
                  </div>
                ))}

                {/* YOUR BUSINESS — not found */}
                <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg" style={{ background: "rgba(239,68,68,0.04)", borderLeft: "3px solid #ef4444", border: "1px dashed rgba(239,68,68,0.25)" }} data-testid="pain-your-business-card">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold shrink-0" style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444" }}>
                    &times;
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold" style={{ color: "#64748B" }}>YOUR BUSINESS</p>
                    <p className="text-xs mt-0.5" style={{ color: "#ef4444" }}>Not found in search results</p>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-1 rounded-full shrink-0" style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444" }}>
                    Invisible
                  </span>
                </div>
              </div>
            </div>
            <p className="text-center text-sm mt-4" style={{ color: "#64748B" }}>
              This happens dozens of times every single day — and you have no idea.
            </p>
          </FadeIn>
        </div>
      </section>


      {/* ═══ SECTION 3: THE COST ═══ */}
      <section
        ref={costRef}
        className="relative text-center"
        style={{ background: "#060D12", padding: "100px 0" }}
        data-testid="cost-section"
      >
        {/* Top divider */}
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)" }} />

        <div className="max-w-3xl mx-auto px-5">
          <FadeIn>
            <p className="text-xs font-bold tracking-[0.25em] uppercase mb-8" style={{ color: "#3ECF8E" }}>
              The cost of being invisible
            </p>
            <p className="font-black tracking-tighter" style={{ fontSize: "clamp(56px, 12vw, 96px)", background: "linear-gradient(180deg, #FFFFFF 30%, #64748B 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }} data-testid="cost-number">
              ${costCount.toLocaleString()}
            </p>
            <p className="mt-2 text-sm font-bold uppercase tracking-wider" style={{ color: "#ef4444" }}>
              Lost every month
            </p>
            <p className="mt-5 text-lg sm:text-xl max-w-xl mx-auto" style={{ color: "#94A3B8", lineHeight: 1.8 }}>
              That's the average. Your audit calculates <span className="text-white font-semibold">your</span> exact number — based on your city, your industry, and your actual Google data.
            </p>
            <div className="mt-10">
              <button
                onClick={() => document.getElementById("hero-section")?.scrollIntoView({ behavior: "smooth" }) || window.scrollTo({ top: 0, behavior: "smooth" })}
                className="inline-flex items-center gap-2 px-8 rounded-xl font-bold text-white text-base transition-all"
                style={{ background: "#6366f1", height: 56 }}
                onMouseOver={e => { e.currentTarget.style.background = "#4f46e5"; }}
                onMouseOut={e => { e.currentTarget.style.background = "#6366f1"; }}
                data-testid="cost-cta-btn"
              >
                See Your Exact Number — Free <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </FadeIn>
        </div>
      </section>


      {/* ═══ SECTION 4: PROOF — ARCTIC AIR CASE STUDY ═══ */}
      <section className="relative" style={{ background: "#0A141A", padding: "80px 0" }} data-testid="story-section">
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(62,207,142,0.3), transparent)" }} />

        <div className="max-w-6xl mx-auto px-5">
          <FadeIn>
            <p className="text-xs font-bold tracking-[0.25em] uppercase mb-4" style={{ color: "#3ECF8E" }}>
              Real Result
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight mb-10">
              From Invisible to #1 in 67 Days
            </h2>
          </FadeIn>

          {/* Bento grid layout */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
            {/* Left: Story card */}
            <FadeIn className="lg:col-span-7" delay={100}>
              <div className="rounded-2xl h-full" style={{ background: "rgba(27,58,75,0.3)", border: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="p-5 sm:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(62,207,142,0.1)" }}>
                      <Award className="w-5 h-5" style={{ color: "#3ECF8E" }} />
                    </div>
                    <div>
                      <p className="text-white font-bold text-sm">Arctic Air HVAC</p>
                      <p className="text-xs" style={{ color: "#64748B" }}>Toronto, ON</p>
                    </div>
                  </div>

                  <div className="space-y-4 text-sm sm:text-base" style={{ color: "#94A3B8", lineHeight: 1.8 }}>
                    <p>When we first audited Arctic Air HVAC, they were ranking <span className="text-white font-semibold">#8</span> for HVAC repair. Customers searching for emergency furnace repair were finding their competitors first.</p>
                    <p>Their audit score: <span className="text-white font-bold">41/100.</span> Estimated monthly loss: <span className="font-bold text-[#ef4444]">$14,200.</span></p>
                    <p>We fixed their Google Business Profile, cleaned up 23 citation inconsistencies, and got them visible in AI search.</p>
                    <p className="text-white font-semibold">67 days later, they ranked #1.</p>
                  </div>

                  {/* Quote */}
                  <div className="mt-6 px-4 py-4 rounded-xl" style={{ borderLeft: "3px solid #6366f1", background: "rgba(99,102,241,0.06)" }}>
                    <p className="text-sm italic" style={{ color: "#94A3B8" }}>
                      "I didn't realize how invisible I was until I saw the report. Best $27 I ever spent."
                    </p>
                    <p className="mt-2 text-xs font-semibold" style={{ color: "#64748B" }}>
                      — Arctic Air HVAC, Toronto ON
                    </p>
                  </div>
                </div>
              </div>
            </FadeIn>

            {/* Right: Stats + Before/After */}
            <div className="lg:col-span-5 flex flex-col gap-4 sm:gap-6">
              {/* Stats row */}
              <FadeIn delay={200}>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {[
                    { label: "Ranking", before: "#8", after: "#1", icon: TrendingUp },
                    { label: "Reviews", before: "31", after: "67", icon: Star },
                    { label: "Maps Calls", before: "—", after: "+312%", icon: Phone },
                    { label: "Monthly Loss", before: "$14,200", after: "$0", icon: DollarSign },
                  ].map((stat, i) => (
                    <div key={i} className="rounded-xl p-4" style={{ background: "rgba(27,58,75,0.3)", border: "1px solid rgba(255,255,255,0.06)" }}>
                      <stat.icon className="w-4 h-4 mb-2" style={{ color: "#3ECF8E" }} />
                      <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: "#64748B" }}>{stat.label}</p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs line-through" style={{ color: "#64748B" }}>{stat.before}</span>
                        <ArrowRight className="w-3 h-3" style={{ color: "#64748B" }} />
                        <span className="text-lg font-black" style={{ color: "#3ECF8E" }}>{stat.after}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </FadeIn>

              {/* Before/After Geo Grids */}
              <FadeIn delay={300}>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  {/* BEFORE grid */}
                  <div className="rounded-xl overflow-hidden" style={{ background: "rgba(27,58,75,0.3)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="before-grid">
                    <div className="px-3 py-2.5" style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                      <p className="font-bold text-white text-xs">Before</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444" }}>41/100</span>
                        <span className="text-[10px]" style={{ color: "#64748B" }}>Rank #8</span>
                      </div>
                    </div>
                    <div className="p-2.5">
                      <div className="grid grid-cols-5 gap-1">
                        {[17,14,9,20,15,12,16,8,7,11,13,9,5,4,6,15,7,4,3,5,18,11,6,5,7].map((v, i) => (
                          <div key={i} className="aspect-square rounded flex items-center justify-center text-[9px] font-bold text-white" style={{ background: v <= 3 ? "#3ECF8E" : v <= 10 ? "#f59e0b" : "#ef4444" }}>{v}</div>
                        ))}
                      </div>
                      <p className="text-[9px] text-center mt-1.5" style={{ color: "#64748B" }}>12% in Top 3</p>
                    </div>
                  </div>
                  {/* AFTER grid */}
                  <div className="rounded-xl overflow-hidden" style={{ background: "rgba(62,207,142,0.05)", border: "1px solid rgba(62,207,142,0.15)" }} data-testid="after-grid">
                    <div className="px-3 py-2.5" style={{ borderBottom: "1px solid rgba(62,207,142,0.1)" }}>
                      <p className="font-bold text-white text-xs">After 67 Days</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "rgba(62,207,142,0.15)", color: "#3ECF8E" }}>89/100</span>
                        <span className="text-[10px]" style={{ color: "#3ECF8E" }}>Rank #1</span>
                      </div>
                    </div>
                    <div className="p-2.5">
                      <div className="grid grid-cols-5 gap-1">
                        {[3,2,1,2,3,2,1,1,1,2,1,1,1,1,1,2,1,1,1,1,3,2,1,1,2].map((v, i) => (
                          <div key={i} className="aspect-square rounded flex items-center justify-center text-[9px] font-bold text-white" style={{ background: "#3ECF8E" }}>{v}</div>
                        ))}
                      </div>
                      <p className="text-[9px] text-center mt-1.5 font-medium" style={{ color: "#3ECF8E" }}>96% in Top 3</p>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>


      {/* ═══ SECTION 5: WHAT YOU GET ═══ */}
      <section style={{ background: "#060D12", padding: "80px 0" }} data-testid="preview-section">
        <div className="absolute left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)" }} />

        <div className="max-w-5xl mx-auto px-5">
          <FadeIn>
            <div className="text-center mb-14">
              <p className="text-xs font-bold tracking-[0.25em] uppercase mb-4" style={{ color: "#3ECF8E" }}>
                Your free audit includes
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight">
                Everything hidden about your<br className="hidden sm:inline" /> local presence — revealed
              </h2>
              <p className="mt-5 text-base sm:text-lg max-w-2xl mx-auto" style={{ color: "#94A3B8", lineHeight: 1.8 }}>
                Most business owners are genuinely shocked by what they find.
              </p>
            </div>
          </FadeIn>

          {/* Feature cards grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
            {[
              { icon: BarChart3, title: "Competitor Leaderboard", desc: "See exactly where you rank against your 3 closest competitors — with real scores.", color: "#6366f1" },
              { icon: Eye, title: "AI Visibility Check", desc: "Does ChatGPT, Claude, or Google AI recommend you when customers ask?", color: "#3ECF8E" },
              { icon: Globe, title: "Citation Health", desc: "Which online directories have wrong information about your business.", color: "#f59e0b" },
              { icon: DollarSign, title: "Revenue Calculator", desc: "How much revenue you're estimated to be missing every single month.", color: "#ef4444" },
            ].map((f, i) => (
              <FadeIn key={i} delay={i * 80}>
                <div className="rounded-xl p-5 sm:p-6 h-full transition-colors" style={{ background: "rgba(27,58,75,0.2)", border: "1px solid rgba(255,255,255,0.05)" }}
                  onMouseOver={e => { e.currentTarget.style.background = "rgba(27,58,75,0.4)"; }}
                  onMouseOut={e => { e.currentTarget.style.background = "rgba(27,58,75,0.2)"; }}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: `${f.color}15` }}>
                    <f.icon className="w-5 h-5" style={{ color: f.color }} />
                  </div>
                  <h3 className="text-white font-bold text-base mb-2">{f.title}</h3>
                  <p className="text-sm" style={{ color: "#94A3B8", lineHeight: 1.7 }}>{f.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Report preview mock */}
          <FadeIn delay={200}>
            <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.08)", boxShadow: "0 8px 60px rgba(0,0,0,0.4)" }}>
              {/* Browser chrome */}
              <div className="px-4 py-2.5 flex items-center gap-2" style={{ background: "rgba(27,58,75,0.5)", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                <div className="flex gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: "#ef4444" }} />
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: "#f59e0b" }} />
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: "#3ECF8E" }} />
                </div>
                <div className="flex-1 mx-3">
                  <div className="rounded-md px-3 py-1 text-xs max-w-xs" style={{ background: "rgba(255,255,255,0.05)", color: "#64748B" }}>
                    audit.rankifylocal.com/report/your-business
                  </div>
                </div>
              </div>
              {/* Report header mock */}
              <div style={{ background: "#1B3A4B" }}>
                <div className="max-w-3xl mx-auto px-5 py-6 sm:px-6 sm:py-8 flex flex-col sm:flex-row items-center gap-5">
                  <div className="relative w-20 h-20 shrink-0">
                    <svg viewBox="0 0 100 100" className="w-full h-full" style={{ transform: "rotate(-90deg)" }}>
                      <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
                      <circle cx="50" cy="50" r="42" fill="none" stroke="#f97316" strokeWidth="8" strokeDasharray={`${(54 / 100) * 264} 264`} strokeLinecap="round" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-xl font-bold text-white">54</span>
                      <span className="text-[9px]" style={{ color: "#64748B" }}>/100</span>
                    </div>
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: "#64748B" }}>Local SEO Audit Report</p>
                    <p className="text-white font-bold text-lg mt-1">Your Business Name</p>
                    <p className="text-sm" style={{ color: "#64748B" }}>Your City, State</p>
                  </div>
                </div>
                {/* KPI bar */}
                <div className="grid grid-cols-4" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                  {[
                    { v: "$5,672", l: "Revenue Lost/mo" },
                    { v: "14", l: "Issues Found" },
                    { v: "91%", l: "Searches Lost" },
                    { v: "#8", l: "Map Ranking" },
                  ].map((k, i) => (
                    <div key={i} className="text-center py-3" style={{ borderRight: i < 3 ? "1px solid rgba(255,255,255,0.05)" : "none" }}>
                      <p className="text-xs sm:text-sm font-bold text-amber-400">{k.v}</p>
                      <p className="text-[9px] sm:text-[10px]" style={{ color: "#64748B" }}>{k.l}</p>
                    </div>
                  ))}
                </div>
              </div>
              {/* Section rows */}
              <div className="p-4 sm:p-6 space-y-3" style={{ background: "#0A141A" }}>
                {["Competitor Leaderboard", "AI Visibility Check", "Citation Health", "Revenue Calculator"].map((s, i) => (
                  <div key={i} className="flex items-center gap-3 p-3.5 rounded-lg" style={{ background: "rgba(27,58,75,0.2)", border: "1px solid rgba(255,255,255,0.04)" }}>
                    <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0" style={{ background: "rgba(99,102,241,0.1)" }}>
                      <div className="w-2.5 h-2.5 rounded-sm" style={{ background: "#6366f1" }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-white">{s}</p>
                      <div className="mt-1.5 h-1.5 rounded-full w-3/4" style={{ background: "rgba(255,255,255,0.06)" }} />
                    </div>
                    <ChevronDown className="w-4 h-4" style={{ color: "#64748B" }} />
                  </div>
                ))}
              </div>
            </div>
            <p className="text-center text-sm mt-4" style={{ color: "#64748B" }}>
              Your actual report — generated in 30 seconds using live Google data
            </p>
          </FadeIn>

          <FadeIn delay={100}>
            <div className="text-center mt-10">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="inline-flex items-center gap-2 px-8 rounded-xl font-bold text-white text-base transition-all"
                style={{ background: "#6366f1", height: 56 }}
                onMouseOver={e => { e.currentTarget.style.background = "#4f46e5"; }}
                onMouseOut={e => { e.currentTarget.style.background = "#6366f1"; }}
                data-testid="preview-cta-btn"
              >
                Run My Free Audit <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </FadeIn>
        </div>
      </section>


      {/* ═══ SECTION 6: FAQ ═══ */}
      <section style={{ background: "#0A141A", padding: "80px 0" }} data-testid="faq-section">
        <div className="max-w-2xl mx-auto px-5">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-10">Quick answers</h2>
          </FadeIn>
          <div className="space-y-3">
            {FAQ_DATA.map((faq, i) => (
              <FadeIn key={i} delay={i * 40}>
                <div className="rounded-xl overflow-hidden" style={{ background: "rgba(27,58,75,0.2)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left transition-colors"
                    style={{ background: openFaq === i ? "rgba(27,58,75,0.3)" : "transparent" }}
                    data-testid={`faq-${i}`}
                  >
                    <span className="font-medium text-white pr-4 text-sm sm:text-base">{faq.q}</span>
                    <ChevronDown className={`w-4 h-4 shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} style={{ color: "#64748B" }} />
                  </button>
                  <div
                    className="overflow-hidden transition-all duration-300"
                    style={{
                      maxHeight: openFaq === i ? 300 : 0,
                      opacity: openFaq === i ? 1 : 0,
                    }}
                  >
                    <div className="px-5 pb-5 text-sm sm:text-base" style={{ color: "#94A3B8", lineHeight: 1.7 }}>{faq.a}</div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>


      {/* ═══ SECTION 7: FINAL CTA ═══ */}
      <section
        className="relative"
        style={{ background: "#060D12", padding: "100px 0", overflow: "hidden" }}
        data-testid="final-cta-section"
      >
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, rgba(99,102,241,0.3), transparent)" }} />
        {/* Ambient glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full opacity-[0.06]" style={{ background: "radial-gradient(circle, #6366f1, transparent 70%)" }} />

        <div className="relative z-10 max-w-xl mx-auto px-5 text-center">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight mb-4">
              Stop Losing Customers to<br />Competitors Who Rank Higher
            </h2>
            <p className="text-base sm:text-lg mb-10" style={{ color: "#94A3B8" }}>
              Find out exactly where you stand — free, in 30 seconds.
            </p>
            <div className="flex justify-center">
              <div className="rounded-2xl p-5 sm:p-7 w-full" style={{ background: "rgba(27,58,75,0.35)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(12px)" }}>
                <HeroForm variant="final" />
              </div>
            </div>
            {/* Trust strip */}
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              {[
                { icon: Shield, text: "No credit card required" },
                { icon: Zap, text: "Results in 30 seconds" },
                { icon: CheckCircle2, text: `${liveCount}+ audits run` },
              ].map((t, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <t.icon className="w-3.5 h-3.5" style={{ color: "#3ECF8E" }} />
                  <span className="text-xs" style={{ color: "#64748B" }}>{t.text}</span>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>


      {/* ═══ SECTION 8: FOOTER ═══ */}
      <footer style={{ background: "#060D12", borderTop: "1px solid rgba(255,255,255,0.06)" }} data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-5 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10">
            {/* Col 1 */}
            <div>
              <Logo size="default" theme="dark" />
              <p className="text-xs font-bold tracking-wider uppercase mt-3" style={{ color: "#64748B" }}>Map Dominance Engine</p>
              <p className="text-sm mt-2" style={{ color: "#94A3B8" }}>Helping local businesses rank #1 on Google Maps</p>
            </div>
            {/* Col 2 */}
            <div>
              <p className="text-sm font-semibold text-white mb-4">Links</p>
              <ul className="space-y-2.5">
                {[
                  { label: "Home", to: "/" },
                  { label: "Run Free Audit", to: "/audit" },
                  { label: "Login", to: "/login" },
                  { label: "Privacy Policy", to: "#" },
                  { label: "Terms of Service", to: "#" },
                ].map((link, i) => (
                  <li key={i}>
                    <Link to={link.to} className="text-sm hover:text-[#6366f1] transition-colors" style={{ color: "#94A3B8" }}>{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
            {/* Col 3 */}
            <div>
              <p className="text-sm font-semibold text-white mb-4">Contact</p>
              <ul className="space-y-2.5 text-sm" style={{ color: "#94A3B8" }}>
                <li>hello@rankifylocal.com</li>
                <li>(647) 694-8882</li>
                <li>212 King St W, 6th Floor<br />Toronto, ON M5H 1K5</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="py-5 text-center text-xs" style={{ borderTop: "1px solid rgba(255,255,255,0.04)", color: "#64748B" }}>
          &copy; {new Date().getFullYear()} RankifyLocal. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
